import {
	FeedPerson,
	FeedRelationship,
	FeedRunCard,
	FeedSpine,
	LEAD_OVERALL_SCORE,
} from "@signalist/shared-types";

import { getSignalPriorityLabel } from "@/pages/Private/pages/ProfileTracker/components/CustomColumns/SignalTimeline/signalTimelineUtils";
import { GenerationJob } from "@/pages/Private/redux/copywritingOrchestration/copywritingOrchestration.slice";
import { getInitials } from "@/utils/getInitials";

export { getInitials };

/** Spine color per strongest card relationship (D9 — semantic tokens only). */
export const SPINE_CLASS: Record<FeedSpine, string> = {
	campaign: "bg-feed-spine-campaign",
	outreach: "bg-feed-spine-outreach",
	connected: "bg-feed-spine-connected",
	none: "bg-feed-spine-none",
};

export type FeedRelationshipKind = "inCampaign" | "previousOutreach" | "connectedOnLinkedin";

/** Same precedence as the card spine: campaign > outreach > connected. */
export const getStrongestRelationship = (
	relationship: FeedRelationship
): FeedRelationshipKind | null => {
	if (relationship.inCampaign) {
		return "inCampaign";
	}

	if (relationship.previousOutreach) {
		return "previousOutreach";
	}

	if (relationship.connectedOnLinkedin) {
		return "connectedOnLinkedin";
	}

	return null;
};

export const RELATIONSHIP_DOT_CLASS: Record<FeedRelationshipKind, string> = {
	inCampaign: "bg-feed-spine-campaign",
	previousOutreach: "bg-feed-spine-outreach",
	connectedOnLinkedin: "bg-feed-spine-connected",
};

export interface PillDisplay {
	label: string;
	className: string;
}

/**
 * Signal-strength pill (S1/S2/S3/DQ) from a signal priority — tolerant of the
 * legacy priority strings via {@link getSignalPriorityLabel}.
 */
export const getStrengthDisplay = (priority: string | null | undefined): PillDisplay => {
	const label = getSignalPriorityLabel(priority);

	if (label === "Priority 1") {
		return { label: "S1", className: "text-text-success border-text-success" };
	}

	if (label === "Priority 2") {
		return { label: "S2", className: "text-text-warning border-text-warning" };
	}

	if (label === "Priority 3") {
		return { label: "S3", className: "text-text-secondary border-gray-300" };
	}

	if (label === "Disqualified") {
		return { label: "DQ", className: "text-status-error border-status-error" };
	}

	return { label: "?", className: "text-text-tertiary border-gray-300" };
};

/**
 * Lead-score ring of the person avatar (read-only in P5, D2); null when unscored. A lead scoring
 * could not judge reads "?", not unscored and not DQ (PREC-4075).
 */
export const getScoreRingDisplay = (score: string | null): PillDisplay | null => {
	if (score === "1") {
		return { label: "P1", className: "text-text-success border-text-success" };
	}

	if (score === "2") {
		return { label: "P2", className: "text-text-warning border-text-warning" };
	}

	if (score === "3") {
		return { label: "P3", className: "text-status-warning border-status-warning" };
	}

	if (score?.toLowerCase() === "disqualified") {
		return { label: "DQ", className: "text-status-error border-status-error" };
	}

	if (score === LEAD_OVERALL_SCORE.UNSURE) {
		return { label: "?", className: "text-text-tertiary border-gray-300" };
	}

	return null;
};

/**
 * Context ids of a signal-feedback POST (SCORE_CHANGE reason / IGNORE, D1+D4):
 * the card's selected person + its tracker organization.
 */
export const buildSignalFeedbackContext = (
	card: FeedRunCard,
	person: FeedPerson | null
): { listPersonId?: number; prospectId?: number; trackerOrganizationId: number } => ({
	...(person?.listPersonId ? { listPersonId: person.listPersonId } : {}),
	...(person?.prospectId ? { prospectId: person.prospectId } : {}),
	trackerOrganizationId: card.company.trackerOrganizationId,
});

/**
 * Type-chip styling derived from the `kind` label by keyword (D9) — the backend
 * has no chip vocabulary, so unknown kinds fall back to a neutral chip.
 */
/**
 * Whether this signal row shows an always-visible AI "state" chip (generating /
 * queued / ready-to-review): the person's job was seeded by this signal and has
 * not been reviewed yet. Mirrors {@link SignalAiCta}'s `renderState` gating — the
 * row uses it to hide the timestamp the absolute chip overlays (mockup
 * `:has(.sig-ref-state) .sig-time`).
 */
export const signalRowHasStateChip = (
	job: GenerationJob | null | undefined,
	signalId: number
): boolean => {
	if (!job || job.seedSignalId !== signalId) {
		return false;
	}

	if (job.status === "RUNNING" || job.status === "QUEUED") {
		return true;
	}

	return job.status === "COMPLETED" && !job.reviewedAt;
};
